import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import StatCard from '../../components/dashboard/StatCard';
import dashboardService, { 
  TraineeStats
} from '../../services/dashboard.service';
import creneauxService, { Creneau } from '../../services/creneaux.service';
import { 
  BookOpen, 
  Award, 
  Calendar, 
  Clock, 
  CheckCircle,
  Search,
  Sparkles,
  Building,
  User,
  MapPin,
  Phone,
  Mail
} from 'lucide-react';
import { Link } from 'react-router-dom';

const TraineeDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState<TraineeStats>({
    enrolledCourses: 0,
    completedCourses: 0,
    certificates: 0,
    totalHours: 0,
    averageScore: 0,
    upcomingSessions: 0
  });
  const [bookedCreneaux, setBookedCreneaux] = useState<Creneau[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoading(true);
        const [statsData, creneauxData] = await Promise.all([
          dashboardService.getTraineeStats(),
          creneauxService.getMyTraineeCreneaux()
        ]);
        setStats(statsData);
        setBookedCreneaux(creneauxData);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadDashboardData();
    }, 30000);

    return () => clearInterval(interval);
  }, []);


  if (isLoading) {
    return (
      <DashboardLayout title={t('trainee.dashboard.title')}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t('trainee.dashboard.title')}>
      <div className="space-y-4">
        {/* Compact Welcome Section */}
        <div className="bg-gradient-to-r from-brand-primary/10 to-brand-accent/10 rounded-2xl p-4 border border-brand-primary/20">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-gradient-to-br from-brand-primary to-brand-primary/80 rounded-xl shadow-lg">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('trainee.dashboard.welcome')}</h2>
              <p className="text-sm text-gray-600">{t('trainee.dashboard.subtitle')}</p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <Link
              to="/trainings"
              className="flex-1 min-w-[140px] inline-flex items-center justify-center px-4 py-3 bg-gradient-to-r from-brand-primary to-brand-primary/80 text-white font-medium rounded-xl hover:from-brand-primary/90 hover:to-brand-primary/70 transition-all shadow-md hover:shadow-lg"
            >
              <Search className="h-4 w-4 mr-2" />
              <span className="text-sm">{t('trainee.dashboard.quickActions.catalog')}</span>
            </Link>
            <Link
              to="/"
              className="flex-1 min-w-[140px] inline-flex items-center justify-center px-4 py-3 bg-gradient-to-r from-brand-secondary to-pink-600 text-white font-medium rounded-xl hover:from-pink-600 hover:to-pink-700 transition-all shadow-md hover:shadow-lg"
            >
              <Building className="h-4 w-4 mr-2" />
              <span className="text-sm">{t('trainee.dashboard.quickActions.home')}</span>
            </Link>
          </div>
        </div>

        {/* Compact Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <StatCard
            title={t('trainee.dashboard.stats.enrolled')}
            value={stats.enrolledCourses}
            change="+2"
            changeType="positive"
            icon={BookOpen}
            color="brand-primary"
          />
          <StatCard
            title={t('trainee.dashboard.stats.completed')}
            value={stats.completedCourses}
            change="+1"
            changeType="positive"
            icon={CheckCircle}
            color="green"
          />
          <StatCard
            title={t('trainee.dashboard.stats.certificates')}
            value={stats.certificates}
            change="+1"
            changeType="positive"
            icon={Award}
            color="brand-accent"
          />
          <StatCard
            title={t('trainee.dashboard.stats.hours')}
            value={`${stats.totalHours}h`}
            change="+8h"
            changeType="positive"
            icon={Clock}
            color="brand-orange"
          />
          <StatCard
            title={t('trainee.dashboard.stats.upcoming')}
            value={stats.upcomingSessions}
            change="+1"
            changeType="positive"
            icon={Calendar}
            color="brand-primary"
          />
          <StatCard
            title={t('trainee.dashboard.stats.centers')}
            value={stats.averageScore}
            change="+2"
            changeType="positive"
            icon={Building}
            color="brand-accent"
          />
        </div>

        {/* Booked Créneaux Section */}
        {bookedCreneaux.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-brand-primary to-purple-600 rounded-xl shadow-md">
                <Calendar className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{t('trainee.dashboard.reservations.title')}</h3>
                <p className="text-sm text-gray-600">{bookedCreneaux.length} {t('trainee.dashboard.reservations.count', { count: bookedCreneaux.length })}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {bookedCreneaux.map((creneau) => (
                <div key={creneau.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-md">
                          <CheckCircle className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{creneau.service.title}</h4>
                          <p className="text-sm text-gray-600">{creneau.service.training_center.name}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="h-4 w-4" />
                          <span>{new Date(creneau.creneau_date).toLocaleDateString('fr-FR', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Clock className="h-4 w-4" />
                          <span>{creneau.start_time} - {creneau.end_time}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="h-4 w-4" />
                          <span>{creneau.service.city || t('common.notSpecified')}</span>
                        </div>
                      </div>
                      
                      {creneau.service.domaine && (
                        <div className="mt-3">
                          <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-brand-primary/10 text-brand-primary border border-brand-primary/20">
                            {creneau.service.domaine.name_french}
                          </span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-3 py-1 text-xs font-medium rounded-lg border ${
                        creneau.booking_status === 'pending' 
                          ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                          : creneau.booking_status === 'approved' || creneau.is_booked
                          ? 'bg-green-100 text-green-700 border-green-200'
                          : 'bg-gray-100 text-gray-700 border-gray-200'
                      }`}>
                        {creneau.booking_status === 'pending' 
                          ? t('trainee.dashboard.reservations.status.pending') 
                          : creneau.booking_status === 'approved' || creneau.is_booked
                          ? t('trainee.dashboard.reservations.status.confirmed')
                          : t('trainee.dashboard.reservations.status.rejected')
                        }
                      </span>
                      <Link
                        to={`/consultant/${creneau.service.training_center.id}`}
                        className="text-xs text-brand-primary hover:text-brand-secondary transition-colors"
                      >
                        {t('trainee.dashboard.reservations.viewConsultant')}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
};

export default TraineeDashboardPage;
